import { event } from "vue-gtag";
import transactionService from "@/services/transactionService";
import {
  navegadorInterno,
  origenLoginActual,
  productoActual,
  productoDeLaRuta,
  recordarOrigenLogin,
} from "@/services/origen";

/**
 * Eventos de conversión de GA4.
 *
 * Hasta ahora se medían cinco eventos, todos de login. Sin `sign_up` ni
 * `purchase` no hay forma de atribuir un registro o una compra a la fuente de
 * tráfico que la trajo: alguien que llega por búsqueda, se registra y paga
 * queda contado como una visita de página.
 *
 * Los nombres y la forma de los parámetros son los que GA4 reconoce de fábrica
 * (`sign_up` con `method`, `purchase` con `transaction_id`, `value`,
 * `currency` e `items`). Con nombres propios habría que armar los informes de
 * comercio electrónico a mano.
 *
 * Lo que describe de DÓNDE viene la persona —el producto de la pantalla, el
 * muro del que salió el login, si el navegador va dentro de otra app— vive en
 * `services/origen.js`. Acá está lo que se manda; allá, lo que se cuenta.
 */

const SENT_PURCHASES_KEY = "utopie:ga4_purchases";
const MAX_REMEMBERED = 50;

/**
 * Los parámetros de un evento, con el producto pegado si se puede saber cuál
 * es.
 *
 * Existe para que el producto no sea algo que haya que acordarse de agregar
 * evento por evento. La regla es una sola: un mismo evento, el producto como
 * parámetro; nunca el producto adentro del nombre. `demo_start_tests` e
 * `inicio_demo_planis` son dos eventos que no se suman ni se comparan y que
 * obligan a rehacer los informes cuando aparece el octavo producto.
 *
 * Si no se sabe el producto no se manda vacío: un parámetro ausente se nota
 * en el informe, uno en blanco parece un dato.
 */
export function conProducto(params, service) {
  const producto = productoActual(service);
  const base = params || {};
  return producto ? Object.assign({}, base, { producto: producto }) : base;
}
/**
 * EL VOCABULARIO DE LA CARA PÚBLICA, del lado del SPA.
 *
 * Es la MISMA tabla que `EVENTOS` en backend/marketing/analitica.py, escrita
 * en el otro idioma porque Python y JavaScript no pueden compartir un archivo.
 * Hay un test que falla si las dos dejan de coincidir.
 *
 * POR QUÉ IMPORTA QUE SEAN LAS MISMAS CLAVES. La portada del SPA y las páginas
 * de Django son dos códigos distintos que miden la misma cosa: alguien mirando
 * un producto, alguien golpeando la puerta para contratar. Si el SPA emitiera
 * `click_demo_portada` y Django `select_content`, un informe de GA4 no podría
 * sumar las dos mitades sin traducir nada a mano — y el informe que hay que
 * armar a mano es el que nadie arma.
 *
 * `fbq` NO entra acá: el píxel no se monta en el SPA (regla de alcance, ver
 * docs/decisiones/arquitectura-cascaron.md). Lo que Meta tiene que ver del
 * lado privado sale del servidor por la Conversions API.
 */
export const EVENTOS_PUBLICOS = {
  demo: "select_content",
  muestra: "select_content",
  descarga_muestra: "file_download",
  planes: "generate_lead",
  contacto_colegio: "generate_lead",
  // `ficha` la emiten solo las páginas de Django —las tarjetas del hub de
  // /producto/—, así que desde acá nunca sale. Está igual porque el test de
  // paridad exige que las dos tablas sean la misma: una excepción documentada
  // en dos archivos es peor que una fila de más, y el día que el SPA tenga su
  // propio listado de productos la clave ya existe.
  ficha: "select_content",
};

/**
 * Un evento de la cara pública, con la misma forma que el puente de base.html.
 *
 * `clave` es la del vocabulario (`demo`, `contacto_colegio`…), no el nombre de
 * GA4: el mapeo vive en un solo lugar y cambiarlo no obliga a tocar a quien
 * llama. Devuelve el nombre de GA4 que salió, o "" si la clave no existe —
 * inventar un evento con el nombre equivocado es peor que no medir.
 */
export function medirPublico(clave, etiqueta, servicio) {
  const nombre = EVENTOS_PUBLICOS[clave];
  if (!nombre) return "";
  // 80 caracteres, igual que el puente de base.html: el texto del botón es lo
  // que separa cuál de los tres caminos a la demo es el que de verdad trabaja.
  const params = {
    content_type: clave,
    item_id: String(etiqueta || "")
      .trim()
      .slice(0, 80),
  };
  const producto = productoActual(servicio);
  // Un parámetro ausente se nota en el informe; uno en blanco parece un dato.
  if (producto) params.producto = producto;
  event(nombre, params);
  return nombre;
}

/**
 * La raíz del SPA dentro del dominio público.
 *
 * Lo que cuelga de acá lo sirve la shell de Vue y se navega con el router; lo
 * demás con ruta absoluta lo sirve Django, y son las páginas del cascarón.
 */
const RAIZ_DEL_SPA = "/utopie/";

/**
 * La FORMA de una página del cascarón: una ruta absoluta con su barra final
 * (`/eureka/`, `/pruebas-de-especialidad/`, `/para/sostenedor/`).
 *
 * La lista de verdad vive en backend/marketing/pages.py y desde el navegador
 * no se puede leer; copiarla acá sería una segunda lista que se queda vieja.
 * En vez de eso hay un test que recorre los enlaces REALES de `_landing.html`,
 * los clasifica con esta misma función y comprueba contra el registro de
 * Django que cada uno que sale como `ficha` sea de verdad la página de un
 * producto —y que ninguno de los que salen callados lo fuera.
 */
const PAGINA_DEL_CASCARON = /^\/[a-z0-9-]+(?:\/[a-z0-9-]+)*\/$/;

/** ¿El enlace lleva a la demo de alguno de los siete productos? */
function llevaAUnaDemo(href) {
  const destino = String(href || "").trim();
  if (destino.indexOf(RAIZ_DEL_SPA) !== 0) return false;
  // Lo que va después de `/utopie/` es la ruta del router, con su `#`. Quien
  // contesta si eso es una demo es `origen.js`, con la lista de productos.
  return !!productoDeLaRuta(destino.slice(RAIZ_DEL_SPA.length));
}

/** ¿Y a la ficha de uno en el cascarón? */
function llevaAUnaFicha(href) {
  const destino = String(href || "").trim();
  // El SPA primero: `/utopie/` también es una ruta absoluta con barra final.
  if (destino.indexOf(RAIZ_DEL_SPA) === 0) return false;
  return PAGINA_DEL_CASCARON.test(destino);
}

/**
 * Un clic en la portada del SPA, medido.
 *
 * POR QUÉ EXISTE. La portada es hoy la página con más tráfico de la cara
 * pública y no medía ni un clic: sus enlaces viven dentro de un shadow root
 * que sirve Django y monta Vue, así que ni el puente de `base.html` los ve
 * —esa plantilla no se carga acá— ni había nada del lado de Vue que los
 * escuchara. El listener ya existía por otra razón (`entrar()` en
 * LandingPage.vue decide a dónde va cada enlace); lo único que faltaba era
 * contar.
 *
 * Recibe el `<a>` y no valores sueltos para que el test pueda llamarla con un
 * enlace igual al real, atributo por atributo.
 *
 * Las dos puertas de contacto —WhatsApp y `mailto:`— valen `generate_lead` y
 * no `select_content`: apuntan a un contrato del Sistema PIE, que es el mismo
 * criterio por el que `contacto_colegio` vale `Lead` en el backend.
 *
 * `conSesion` calla la NAVEGACIÓN, y sólo la navegación. Del 22 al 23-sep-2026
 * la portada emitió 546 `select_content` con `content_type=demo` —27 en el mes
 * anterior— y unos 420 eran usuarias recurrentes apretando "Plataforma PIE"
 * para ir a trabajar. Ese evento mide interés de quien TODAVÍA no es cliente,
 * así que quien ya lo es no puede contar en él; y esa navegación no se pierde,
 * GA4 ya tiene el `page_view` de /redpie.
 *
 * Las puertas de contacto NO se callan, y por eso la distinción vive acá y no
 * en quien llama: una cuenta que escribe desde el bloque de contacto puede ser
 * un colegio que viene a contratar como persona natural, que es exactamente el
 * lead que se busca. Quien llama tendría que reconocer WhatsApp y `mailto:`
 * para decidirlo, o sea repetir lo de abajo — una segunda fuente de verdad
 * para algo que esta función ya sabe.
 *
 * EL DESTINO MANDA, NO EL `data-servicio`. Hasta el 25-sep-2026 cualquier
 * `<a data-servicio>` valía `demo`, mirara a donde mirara, y en
 * `_landing.html` hay 31 de esos enlaces: 20 llevan a una demo, 8 son anclas
 * que bajan en la misma página —el ícono «Plataforma PIE» de la botonera,
 * «Ver la Plataforma PIE»—, 2 llevan a la ficha de un producto en el cascarón
 * y 1 es «¿Tu colegio ya la tiene? Entra con tu cuenta». Con el #923 puesto
 * seguían saliendo unos 40 «clics a la demo de Plataforma PIE» al día contra
 * UNA vista de `/utopie/#/pie-demo`: esos 40 eran los otros once enlaces.
 *
 * Lo que sale de cada forma:
 *   · una ruta `-demo` → `demo`, como siempre.
 *   · una página del cascarón → `ficha`, el MISMO vocabulario que las tarjetas
 *     del hub de /producto/ (#914): son la misma pregunta —quién fue a leer
 *     sobre un producto— y tienen que sumarse en el mismo informe.
 *   · un ancla interna → NADA. Bajar en la misma página no es visitar nada.
 *   · `/utopie/` («Entra con tu cuenta») → NADA. Ese es el embudo del login y
 *     ya tiene sus propios eventos.
 *
 * El silencio es el default: lo que no se reconoce no emite. Un evento de más
 * es peor que uno de menos —infla el numerador de cada tasa de conversión y no
 * se nota nunca—, y esta función es la que lo demostró dos veces.
 */
export function medirClicDePortada(a, conSesion) {
  if (!a || typeof a.getAttribute !== "function") return "";
  const href = a.getAttribute("href") || "";
  const etiqueta = a.textContent || "";
  if (/^(https?:\/\/)?(www\.)?(wa\.me|api\.whatsapp\.com)/i.test(href) ||
      /^mailto:/i.test(href)) {
    // La portada habla del Sistema PIE: quien escribe por WhatsApp desde acá
    // no está preguntando por un producto suelto.
    return medirPublico("contacto_colegio", etiqueta, "pie");
  }
  const servicio = a.getAttribute("data-servicio");
  // El `data-servicio` ya no decide QUÉ evento sale —eso lo decide el
  // destino—, pero sigue siendo de dónde sale el producto: un evento sin esa
  // dimensión no se puede comparar con los de las otras demos.
  if (!servicio) return "";
  // Acá, después de las puertas: con sesión esto no es mirar un producto, es
  // ir a trabajar. Vale igual para la ficha que para la demo.
  if (conSesion) return "";
  if (llevaAUnaDemo(href)) return medirPublico("demo", etiqueta, servicio);
  if (llevaAUnaFicha(href)) return medirPublico("ficha", etiqueta, servicio);
  // Un ancla interna, `/utopie/`, o cualquier cosa que no se reconozca.
  return "";
}

/**
 * El formulario de contacto de la portada, enviado.
 *
 * Es la conversión principal de esa página y hasta el 17-sep-2026 no existía
 * para ninguna de las cuatro fuentes. La fila y el `Lead` de Meta los deja el
 * servidor (`InformationViewSet.send_email` → `registrar_cotizacion`); esto es
 * la mitad de GA4.
 */
export function trackLeadDeLaPortada() {
  return medirPublico("planes", "Enviar", "pie");
}

/**
 * Una invitación compartida.
 *
 * POR QUÉ EXISTE. El programa de referidos no dejaba ni un evento: ni la
 * tarjeta que arma el mensaje, ni el banner que lo recibe, ni la oferta del
 * menú. La base sabe quién invitó a quién —la vista `marketing.invitaciones`,
 * `tipo='referido'`— pero eso es el final del embudo: cuánta gente abre la
 * tarjeta y comparte, y por qué vía, no se sabía.
 *
 * `share` es un evento de fábrica de GA4 (está en `GA4_ESTANDAR` de
 * backend/marketing/analitica.py) y `method` es el parámetro con el que GA4
 * espera "por dónde lo compartió". Por eso NO entra en `EVENTOS_PUBLICOS`:
 * esa tabla es el vocabulario propio de la cara pública, el que hay que
 * traducir; esto ya se llama como se llama.
 *
 * Lo que NO se mide, a propósito: que alguien LLEGUE por una invitación. Eso
 * queda en el `utm_source=referido` del enlace, que `OrigenMiddleware` guarda
 * del lado del servidor, y el canje ya queda en la base. Un evento ahí sería
 * contar dos veces lo mismo, y con peor dato.
 */
export function medirInvitacionCompartida(metodo, service) {
  event(
    "share",
    conProducto({ method: metodo, content_type: "invitacion" }, service)
  );
  return "share";
}

/**
 * La demostración de un producto: empezó, y terminó.
 *
 * POR QUÉ EXISTEN. El embudo de la cara pública se cortaba justo en el medio.
 * Se medía el clic que lleva a una demo (`select_content`) y se volvía a medir
 * recién en `sign_up`; entre los dos está la única pantalla que muestra el
 * producto trabajando, y no emitía nada. La pregunta que eso dejaba sin
 * respuesta no es cuántos entran: es cuántos llegan al final. Mirar treinta
 * segundos y cerrar, y terminar la demostración, son dos visitas que se
 * contaban igual.
 *
 * Los nombres son los de fábrica de GA4 y no propios: con `demo_terminada` el
 * informe de embudo hay que armarlo a mano, y el que hay que armar a mano es
 * el que nadie arma. No entran a `EVENTOS_PUBLICOS`: esa tabla es la que el
 * backend espeja para las páginas de Django, y esto solo existe en el SPA.
 *
 * `method` distingue quién arrancó la demostración, y solo lo manda la que
 * tiene dos formas de arrancar: el menú de Materiales para la clase se elige
 * solo a los seis segundos si el visitante no toca nada, y sin separarlo ese
 * arranque automático infla «empezaron la demo» con gente que no eligió. Es
 * la misma dimensión de `sign_up`, no una nueva.
 */
export function medirDemoIniciada(servicio, metodo) {
  // Un parámetro ausente se nota en el informe; uno en blanco parece un dato.
  const params = metodo ? { method: metodo } : {};
  event("tutorial_begin", conProducto(params, servicio));
  return "tutorial_begin";
}

export function medirDemoTerminada(servicio) {
  event("tutorial_complete", conProducto({}, servicio));
  return "tutorial_complete";
}

/**
 * EL PRODUCTO DE UN GOLDEN TICKET. Siempre el mismo: los tickets regalan usos
 * de corrección de tests y su QR aterriza en `#/test-correction-demo`, donde
 * `conProducto` lo deduce solo de la ruta. Va igual como respaldo, porque la
 * tarjeta vive en el layout y sigue en pantalla si la persona navega a otra
 * parte del SPA antes de canjear.
 */
const SERVICIO_DEL_TICKET = "Corregir Test";

/**
 * Los códigos de ticket ya contados en esta visita.
 *
 * Un `Set` y no un booleano: el evento es «se vio la tarjeta de ESTE código»,
 * y contar dos veces el mismo código —la tarjeta se vuelve a preguntar al
 * servidor cuando alguien cierra sesión con ella en pantalla— inventaría un
 * escaneo que no ocurrió. Se vacía sola al recargar, que es exactamente lo
 * que «una visita» quiere decir acá.
 */
const ticketsContados = new Set();

/**
 * Alguien llegó con un golden ticket: en qué estado estaba.
 *
 * POR QUÉ LOS CUATRO Y NO SOLO EL QUEMADO. El PDF del ticket se imprime y se
 * reenvía a grupos, así que el mismo código lo escanea gente que no es su
 * dueña; cuánta, ES la medida de cuánto circula cada ticket. Pero el total de
 * escaneos por lote —que es la otra pregunta: cuántos de los tickets que
 * repartimos llegaron a usarse— solo sale si se cuentan los cuatro estados.
 * Con el estado dentro de `content_type` se cuentan juntos sin mezclarse.
 *
 * OJO CON `invalido`: es ruido, no un estado comparable con los otros tres.
 * Junta el código mal copiado del papel con el que nunca existió, y las dos
 * cosas son errores de transcripción, no visitas de un ticket. No se suma con
 * `canjeado` y `vencido` para decir «tickets ajenos vistos».
 *
 * El código va en `item_id` y eso está bien mirado: un golden ticket es un
 * cupón al portador, no una persona. Es lo que permite preguntar por cuántas
 * manos pasó UNO.
 */
export function medirTicketVisto(estado, codigo) {
  if (!estado || ticketsContados.has(codigo)) return "";
  ticketsContados.add(codigo);
  event(
    "select_content",
    conProducto(
      { content_type: "ticket_" + estado, item_id: codigo },
      SERVICIO_DEL_TICKET
    )
  );
  return "select_content";
}

/**
 * Los dos enlaces a Instagram de la tarjeta de ticket muerto.
 *
 * POR QUÉ NO SALEN SOLOS. GA4 mide clics salientes con la medición mejorada,
 * y en este sitio no llega ninguno: cero eventos `click` en 30 días. Sin
 * esto, quien rebota y quien se va a buscar su propio ticket se ven igual —y
 * esa diferencia es la pregunta entera de la campaña.
 *
 * `cual` es `"cuenta"` (seguir el perfil) o `"publicacion"` (el post del
 * lote, que viene del ticket). Sin tope por visita, al revés que la tarjeta:
 * acá cada clic es un clic, y dos son dos.
 */
export function medirTicketInstagram(cual) {
  event(
    "select_content",
    conProducto(
      { content_type: "ticket_instagram", item_id: cual },
      SERVICIO_DEL_TICKET
    )
  );
  return "select_content";
}

/**
 * Un golden ticket canjeado.
 *
 * `earn_virtual_currency` es el nombre de fábrica de GA4 para «se ganó
 * crédito», con `virtual_currency_name` (qué) y `value` (cuánto). Con un
 * nombre propio el informe habría que armarlo a mano.
 *
 * `value` SON USOS, NO PESOS. No entra en los ingresos de GA4 —eso pide
 * `currency`, que acá no va— pero sí suma en la métrica genérica «valor del
 * evento»: mirar esa métrica sin filtrar por evento es sumar usos regalados
 * con otra cosa.
 *
 * `method` separa a quien canjeó un ticket después de haber visto uno ajeno
 * ya muerto (`tras_ticket_usado`) de quien llegó directo con el suyo
 * (`directo`). Es la misma dimensión de `sign_up`, no una nueva.
 *
 * Esto NO reemplaza a la base. El canje queda en `GoldenTicket.redeemed_by`,
 * que es mejor dato y no lo pierde ningún bloqueador; el evento existe para
 * poder cerrar el embudo dentro de GA4, donde están los pasos anteriores.
 */
export function medirTicketCanjeado(usos, metodo) {
  event(
    "earn_virtual_currency",
    conProducto(
      {
        virtual_currency_name: "usos_test_correction",
        value: Number(usos) || 0,
        method: metodo,
      },
      SERVICIO_DEL_TICKET
    )
  );
  return "earn_virtual_currency";
}

/**
 * Un código promocional canjeado desde su enlace con QR (`utopie.cl/d/<codigo>`).
 *
 * Mismo evento que `medirTicketCanjeado`, con otro `method`: así el informe de
 * GA4 separa el QR de los golden tickets sin un evento nuevo. `value` SON
 * USOS, no pesos, y `virtual_currency_name` dice de qué servicio
 * (`documents_uses`, `tests_uses`…). El dato bueno sigue siendo la base
 * (`RedeemedCode`).
 */
const PRODUCTO_DEL_CAMPO = {
  documents_uses: "Documentos PIE",
  tests_uses: "Corregir Test",
};

export function medirCodigoCanjeado(usos, campo) {
  event(
    "earn_virtual_currency",
    conProducto(
      {
        virtual_currency_name: campo || "usos",
        value: Number(usos) || 0,
        method: "codigo_promocional",
      },
      PRODUCTO_DEL_CAMPO[campo]
    )
  );
  return "earn_virtual_currency";
}

/**
 * Los eventos del embudo de login. Todos salen por `eventoLogin`.
 */
export const EVENTOS_DE_LOGIN = [
  "login_wall_shown",
  "login_click",
  "login_attempt",
  "login_success",
  "login_failure",
  "sign_up",
  // No empieza con `login_` pero es del mismo embudo: sin `origen_login` no se
  // podria saber de que muro salio quien uso el aviso.
  "aviso_navegador_interno",
];

/**
 * Por qué un intento de login no terminó en sesión, en una palabra.
 *
 * Existe porque en GA4 la mayoría de los `login_failure` llega con
 * `reason` vacío, y un vacío ahí se muestra como «(not set)»: una fila que no
 * separa una contraseña mal escrita de un 500 nuestro ni de un teléfono que se
 * quedó sin red. Los tres se arreglan de maneras distintas y los tres se veían
 * iguales.
 *
 * El vocabulario es corto a propósito —`error_<código>`, `server_error`,
 * `network_error`, `desconocido`— porque es lo que se va a agrupar en el
 * informe. Los 5xx se juntan bajo un solo nombre (son nuestros y se miran en
 * el log del servidor, no acá); los 4xx conservan su número, que es lo que
 * distingue una sesión caducada de una cuenta rechazada.
 *
 * `error.request` sin `error.response` es como axios dice «salió y no volvió
 * nadie», o sea la red. Y si no hay ni eso, igual sale una palabra: nunca un
 * vacío.
 */
export function motivoDeFallo(error) {
  const status = error && error.response && error.response.status;
  if (status) return status >= 500 ? "server_error" : "error_" + status;
  if (error && (error.request || error.message)) return "network_error";
  return "desconocido";
}

/**
 * Un evento del embudo de login, con todo lo que tiene que llevar.
 *
 * Existe para que `navegador_interno` y `origen_login` no sean algo que haya
 * que acordarse de agregar en cada uno de los quince sitios que emiten estos
 * eventos. La regla es que ningún `event("login_…")` se escriba a mano; hay un
 * test que falla si aparece uno.
 */
export function eventoLogin(nombre, params, service) {
  const datos = Object.assign({}, params || {});
  // El muro es el que sabe de dónde viene; los eventos que siguen lo heredan.
  if (nombre === "login_wall_shown" && datos.origen_login) {
    recordarOrigenLogin(datos.origen_login);
  } else if (!datos.origen_login && origenLoginActual()) {
    datos.origen_login = origenLoginActual();
  }
  datos.navegador_interno = navegadorInterno();
  // Un fallo sin motivo no se puede leer, y no puede depender de que los
  // quince sitios que emiten esto se acuerden. Cada uno pone el suyo —hay un
  // test que lo comprueba— y esto es lo que queda si alguno se olvida.
  if (nombre === "login_failure" && !datos.reason) datos.reason = "desconocido";
  event(nombre, conProducto(datos, service));
}

function readSent() {
  try {
    const raw = window.localStorage.getItem(SENT_PURCHASES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // Modo incógnito o storage lleno: mejor medir de más que romper la página.
    return [];
  }
}

function rememberSent(id) {
  try {
    const sent = readSent();
    sent.push(String(id));
    window.localStorage.setItem(
      SENT_PURCHASES_KEY,
      JSON.stringify(sent.slice(-MAX_REMEMBERED))
    );
  } catch (e) {
    /* ver readSent */
  }
}

function alreadySent(id) {
  return readSent().indexOf(String(id)) !== -1;
}

/**
 * Registro completado.
 *
 * Hay dos caminos de alta y los dos cuentan:
 *
 *   - Google Sign-In (`LoginModalComponent`, `GoogleTrialButton`), que solo
 *     mide cuando el backend devuelve `is_new_user`, porque el mismo botón
 *     sirve para entrar y para registrarse.
 *   - El formulario "Registrarse" (`authMixin.register_user`), que llega acá
 *     cuando `/register/` devuelve 200.
 *
 * Este comentario decía que Google era el único camino, y por eso el
 * formulario nunca llamó acá: del 25 al 31-ago-2026 la base registró 27 altas
 * y GA4 vio 5. La medición no falla ruidosamente — cuenta de menos y calla.
 */
export function trackSignUp(method, service) {
  // Sin `method` no se manda un valor por defecto: un `sign_up` sin método se
  // nota en el informe, uno etiquetado mal no. Antes el defecto era "google",
  // que es justo la etiqueta equivocada para el alta que faltaba.
  //
  // El producto lo resuelve `conProducto` en vez de pedírselo a cada llamador:
  // es lo que contesta "qué demo crea cuentas", que es la pregunta con la que
  // se decide dónde poner el próximo peso de publicidad.
  //
  // Por `eventoLogin` como el resto del embudo: `sign_up` es su último paso y
  // tiene que poder cruzarse con los anteriores por `origen_login` y por
  // `navegador_interno`.
  eventoLogin("sign_up", method ? { method: method } : {}, service);
}

/**
 * Compra autorizada.
 *
 * `transaction` es lo que devuelve `transactions/last_transaction/`.
 *
 * El evento se dispara del lado del cliente porque la autorización de Webpay
 * vuelve por un redirect del servidor y el SPA es el que recupera el hilo.
 * Eso trae un problema: la pantalla de éxito se dispara con
 * `?transaction_success=1`, que sobrevive a un F5. Sin la lista de ids ya
 * enviados, cada recarga contaría una compra nueva y los ingresos de GA4
 * quedarían inflados.
 */
export function trackPurchase(transaction) {
  if (!transaction || !transaction.id) return false;
  if (alreadySent(transaction.id)) return false;

  rememberSent(transaction.id);

  event("purchase", {
    transaction_id: String(transaction.id),
    value: Number(transaction.total) || 0,
    currency: "CLP",
    items: [
      {
        item_id: transaction.product_code || "",
        item_name: transaction.product_name || "",
        price: Number(transaction.product_price) || 0,
        quantity: Number(transaction.amount) || 1,
      },
    ],
  });
  return true;
}

/**
 * Igual que trackPurchase, pero yendo a buscar la transacción al servidor.
 *
 * Lo usa la compra con tarjeta inscrita (Oneclick), que se autoriza en la
 * misma llamada y por eso nunca pasa por la pantalla de éxito.
 */
export function trackPurchaseFromServer() {
  return transactionService
    .getLastTransaction()
    .then((data) => trackPurchase(data))
    .catch(() => false);
}

export default {
  conProducto,
  eventoLogin,
  medirPublico,
  medirClicDePortada,
  motivoDeFallo,
  trackLeadDeLaPortada,
  trackSignUp,
  trackPurchase,
  trackPurchaseFromServer,
};
